import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DashboardCard, StatsGrid, StatItem, DataList, DataListItem } from '../../../shared/ui';
import { paymentsV2Api, PaymentV2, PaymentV2Filters } from '../../../api/paymentsV2Api';
import { useAuth } from '../../../shared/contexts/AuthContext';
import UserPaymentsFilter from '../components/UserPaymentsFilter';
import './UserPaymentsCard.css';

const UserPaymentsCard: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [payments, setPayments] = useState<PaymentV2[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<PaymentV2Filters>({});
  const { user } = useAuth();

  const loadPayments = useCallback(async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      // Если период не выбран, сервер вернет последние 30 платежей по умолчанию
      const paymentsData = await paymentsV2Api.getUserPayments(filters);
      setPayments(paymentsData);
    } catch (error) {
      console.error('Ошибка при загрузке платежей пользователя:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, filters]);

  useEffect(() => {
    if (!isCollapsed) {
      loadPayments();
    }
  }, [isCollapsed, loadPayments]);

  const handleFiltersChange = (newFilters: PaymentV2Filters) => {
    setFilters(newFilters);
  };

  // Подготовка элементов для DataList
  const dataListItems: DataListItem[] = useMemo(() => {
    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: 'RUB',
        minimumFractionDigits: 0
      }).format(amount);
    };

    const formatDate = (dateString: string) => {
      return new Date(dateString).toLocaleDateString('ru-RU');
    };

    const items: DataListItem[] = [];
    
    // Группируем платежи по дате
    const grouped: { [key: string]: PaymentV2[] } = {};
    
    payments.forEach(payment => {
      const dateKey = formatDate(payment.date);
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(payment);
    });
    
    const sortedGroups = Object.entries(grouped).sort((a, b) => 
      new Date(b[0].split('.').reverse().join('-')).getTime() - 
      new Date(a[0].split('.').reverse().join('-')).getTime()
    );

    sortedGroups.forEach(([date, datePayments]) => {
      // Добавляем заголовок даты
      const totalAmountForDate = datePayments.reduce((sum, p) => sum + p.amount, 0);
      items.push({
        id: `date-${date}`,
        title: date,
        subtitle: `${datePayments.length} платеж(ей) • ${formatCurrency(totalAmountForDate)}`,
        icon: 'ri-calendar-line',
        isHeader: true,
        className: 'payment-date-header'
      });

      // Добавляем элементы платежей
      datePayments.forEach((payment) => {
        items.push({
          id: payment.id.toString(),
          title: formatCurrency(payment.amount),
          icon: payment.signatureAdmin && payment.signatureUser ? 'ri-checkbox-circle-line' : 'ri-time-line',
          details: [
            {
              text: `Сумма: ${formatCurrency(payment.amount)}`,
              className: 'payment-detail'
            }
          ],
          className: payment.signatureAdmin && payment.signatureUser ? 'signed' : 'pending'
        });
      });
    });

    return items;
  }, [payments]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getTotalAmount = () => {
    return payments.reduce((sum, payment) => sum + payment.amount, 0);
  };

  const getSignedPayments = () => {
    return payments.filter(p => p.signatureAdmin && p.signatureUser).length;
  };

  const getPendingPayments = () => {
    return payments.filter(p => !p.signatureAdmin || !p.signatureUser).length;
  };

  const statsData: StatItem[] = [
    {
      id: 'total',
      value: formatCurrency(getTotalAmount()),
      label: 'Всего',
      icon: 'ri-money-dollar-circle-line',
      color: 'muted'
    },
    {
      id: 'count',
      value: payments.length.toString(),
      label: 'Платежей',
      icon: 'ri-file-list-line',
      color: 'muted'
    },
    {
      id: 'signed',
      value: getSignedPayments().toString(),
      label: 'Подписано',
      icon: 'ri-checkbox-circle-line',
      color: 'success'
    },
    {
      id: 'pending',
      value: getPendingPayments().toString(),
      label: 'Ожидает',
      icon: 'ri-time-line',
      color: 'warning'
    }
  ];

  return (
    <DashboardCard
      id="user-payments-card"
      title="Мои платежи"
      icon="ri-wallet-line"
      isCollapsible={true}
      isCollapsed={isCollapsed}
      onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      className="user-payments-card"
    >
      {isLoading ? (
        <div className="user-payments-loading">
          <div className="loading-spinner">
            <i className="ri-loader-4-line"></i>
          </div>
          <span>Загрузка платежей...</span>
        </div>
      ) : (
        <>
          <StatsGrid items={statsData} />

          {/* Фильтры */}
          <UserPaymentsFilter
            filters={filters}
            onFiltersChange={handleFiltersChange}
            loading={isLoading}
          />

          <DataList
            items={dataListItems}
            variant="detailed"
            showDividers={true}
            headerStyle="minimal"
            emptyState={{
              icon: 'ri-inbox-line',
              title: 'Нет платежей',
              subtitle: 'У вас пока нет платежей'
            }}
          />
        </>
      )}
    </DashboardCard>
  );
};

export default UserPaymentsCard;

